// Third-party
import { useTranslation } from 'react-i18next';

// Redux-toolkit
import { FC, useEffect, useState } from 'react';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import { MemoizedBaseAvatar, TDialogAction, BaseDialog } from '@mgm/common/components/base';
import { TMember } from '../../../types';
import { DeleteIcon } from '../../team/components/CreateMember/DeleteIcon';
import { useAppDispatch, useAppSelector } from '../../../hooks/useAppStore';
import { commissionsActions } from '../../../store/commissions/slice';
import { getMemberEmails, getSelectedCommissionPlan } from '../../../store/commissions/selectors';
import { useCommissions } from '../../../hooks/useCommissions';
import { TAddCommissionPlanReq } from '../../../types/requests';

type TOverrideDialog = {
  isOverrideDialogOpen: boolean,
  setIsOverrideDialogOpen: (val: boolean) => void
  membersWithExistingPlans: Array<TMember>
};

const OverrideDialog: FC<TOverrideDialog> = ({
  isOverrideDialogOpen,
  setIsOverrideDialogOpen,
  membersWithExistingPlans,
}) => {
  const { t } = useTranslation(['common', 'commissions', 'team']);
  const dispatch = useAppDispatch();
  const memberEmails = useAppSelector(getMemberEmails);
  const [localMembers, setLocalMembers] = useState(membersWithExistingPlans);
  const {
    preparePayload,
    prepareFixedCommissionData,
    prepareCommissionStepsData,
    prepareProductCommissionData,
    handlePlanMutation,
    isLoading,
  } = useCommissions();

  const selectedCommissionPlan = useAppSelector(getSelectedCommissionPlan);

  useEffect(() => {
    setLocalMembers(membersWithExistingPlans);
  }, [membersWithExistingPlans]);

  const handleCloseDialog = () => {
    setIsOverrideDialogOpen(false);
  };

  const assignMembers = async () => {
    const payload = preparePayload();
    let data;

    if (selectedCommissionPlan?.type === 'Fixed') {
      data = prepareFixedCommissionData(payload);
    } else if (selectedCommissionPlan.type === 'Steps') {
      data = prepareCommissionStepsData(payload);
    } else {
      data = prepareProductCommissionData(payload);
    }

    await handlePlanMutation(data as Omit<TAddCommissionPlanReq['planData'], 'creator' & 'updatedBy'>);
  };

  const dialogActions: Array<TDialogAction> = [
    {
      label: t('cancel'),
      variant: 'outlined',
      color: 'primary',
      isDisabled: isLoading,
      onClick: () => handleCloseDialog(),
    },
    {
      label: t('continue', { ns: 'team' }),
      color: 'primary',
      isLoading,
      onClick: () => assignMembers(),
    },
  ];

  const deleteMember = (member: TMember) => {
    setLocalMembers(localMembers.filter((localMember) => localMember.email !== member.email));
    dispatch(commissionsActions.setMemberEmails(
      memberEmails.filter((email) => email !== member.email),
    ));
  };

  return (
    <BaseDialog
      dialogWidth={500}
      dialogContent={(
        <Stack>
          {t('theFollowingMembers', { ns: 'commissions' })}
          <Box pt={3}>
            <List>
              {localMembers.map((member) => (
                <ListItem key={member.email}>
                  <MemoizedBaseAvatar avatarName={member.name} avatarFile={member.avatarFile} />
                  <ListItemText primary={member.name} />
                  <DeleteIcon size={0.8} item={member} onClick={deleteMember} />
                </ListItem>
              ))}
            </List>
          </Box>
        </Stack>
      )}
      isOpenDialog={isOverrideDialogOpen}
      dialogTitle={t('overrideExising', { ns: 'commissions' })}
      handleClose={handleCloseDialog}
      dialogActions={dialogActions}
    />
  );
};

export {
  OverrideDialog,
};
